<template>
  <div>
    <!--- DELETE ORGANIZATION MODAL ---->
    <Modal
      width="400"
      v-model="displayDeleteOrganizationModal"
      :title="$t('settings.delete_organization_modal_title')"
      :subtitle="$t('settings.delete_organization_modal_subtitle')"
      :dialog-icon="'error'"
      :cta-text="$t('settings.delete_organization_modal_cta_text')"
      :cta-color="'error'"
      :cta-loading="deleteOrganizationLoading"
      :cta-disabled="confirmationWord !== ORGANIZATION_DELETE_CONFIRMATION_WORD"
      @confirm="deleteOrganization"
      @cancel="closeDeleteOrganizationModal">
      <p class="delete-confirm">
        {{ $t("settings.delete_organization_modal_confirmation", { word: ORGANIZATION_DELETE_CONFIRMATION_WORD }) }}
      </p>
      <v-text-field class="delete-confirm__input" v-model="confirmationWord" hide-details label=""> </v-text-field>
    </Modal>
    <PaywallLimitModal
      v-model="displayPaywallLimitModal"
      :title="$t('settings.paywall_limit_modal.title')"
      :cta-text="$t('settings.paywall_limit_modal.cta_text')"
      :subtitle="$t('settings.paywall_limit_modal.subtitle')"
      :editor-subtitle="$t('settings.paywall_limit_modal.editor_subtitle')"
      @confirm="
        () => {
          $router.replace({ query: { tab: 'billing_payments' } });
          displayPaywallLimitModal = false;
        }
      "></PaywallLimitModal>
    <!---  COMPANY DETAILS CARD --->
    <v-card class="mt-6 spacing-bottom--24">
      <v-form v-model="isFormValid">
        <div class="d-flex">
          <div class="d-flex flex-column">
            <v-card-title class="spacing-bottom--16">{{ $t("settings.workplace_users_title") }}</v-card-title>
          </div>
          <v-card-actions class="ml-auto">
            <v-btn
              data-cy="update-company-details"
              class="company-save-btn"
              :disabled="!$can('update', 'organization name') || !isFormValid"
              :loading="updateCompanyLoading"
              @click="updateCompanyDetails"
              color="primary"
              outlined
              >Save</v-btn
            >
          </v-card-actions>
        </div>
        <va-text-input
          data-cy="company-name-input"
          :disabled="!$can('update', 'organization name')"
          class="spacing-bottom--16"
          v-model="companyData.name"
          required
          :label="$t('settings.workplace_users_company_name')"
          :placeholder="$t('settings.workplace_users_company_name_placeholder')"></va-text-input>
      </v-form>

      <div class="delete-organization-block d-flex align-center" v-if="$can('delete', 'organization')">
        <div class="d-flex flex-column">
          <span class="delete-organization-block__title">{{ $t("settings.delete_organization_title") }}</span>
          <span class="delete-organization-block__subtitle">
            {{ $t("settings.delete_organization_subtitle") }}
          </span>
        </div>
        <div class="delete-organization-block__actions ml-auto">
          <v-btn
            :loading="deleteOrganizationLoading"
            @click="displayDeleteOrganizationModal = true"
            outlined
            color="body"
            >{{ $t("settings.delete_organization_btn_title") }}</v-btn
          >
        </div>
      </div>
    </v-card>

    <!-- INVITE USER MODAL -->
    <Modal
      width="431"
      v-model="inviteUserModalDisplayed"
      :title="$t('settings.workplace_users_modal_title')"
      :subtitle="$t('settings.workplace_users_modal_subtitle')"
      :cta-text="$t('settings.workplace_users_modal_cta_text')"
      :cta-disabled="inviteUserBtnDisabled"
      :cta-loading="inviteUserLoading"
      :cta-cy-text="'invite-user-confirm-btn'"
      persistent
      actions-border
      @confirm="inviteUser"
      @cancel="resetInviteUserData">
      <div class="invite-user__content-wrapper">
        <va-text-input
          data-cy="invite-user-email-input"
          class="spacing-bottom--16"
          v-model="inviteUserData.email"
          :rules="[v => !!validateEmail(v) || this.$t('generic.validation.email')]"
          :label="$t('settings.workplace_users_invite_email')"
          :placeholder="$t('settings.workplace_users_invite_email_placeholder')"></va-text-input>
        <va-select-input
          v-model="inviteUserData.permission"
          data-cy="invite-user-permissions-input"
          :label="$t('settings.workplace_users_invite_role')"
          :placeholder="$t('settings.workplace_users_invite_role_placeholder')"
          :choices="organizationRoles"
          :item-text="'title'"
          :item-value="'@id'"
          :prepend-inner-icon="'mdi-pencil-outline'"
          :hide-details="'auto'"
          @change="selectedRoleHandler">
        </va-select-input>
        <p class="invite-user__role-disclaimer spacing-bottom--16">
          {{ $t(this.selectedRole.disclaimer) }}
        </p>
        <va-autocomplete-input
          ref="domainsInput"
          class="mt-4"
          data-cy="invite-user-domains-input"
          v-model="inviteUserData.domains"
          :label="$t('settings.workplace_users_invite_domains')"
          :placeholder="$t('settings.workplace_users_invite_domains_placeholder')"
          :choices="domainsList"
          :item-text="'name'"
          :item-value="'@id'"
          :hide-details="'auto'"
          cache-items
          @change="selectedDomainsHandler"
          @blur="handleDomainsBlur"
          multiple
          hide-select-all>
        </va-autocomplete-input>
      </div>
    </Modal>

    <!--- ORGANIZATION USERS LIST --->
    <v-card class="px-0 py-0">
      <div class="d-flex px-4 py-4">
        <div class="d-flex flex-column">
          <v-card-title>{{ $t("settings.workplace_users_list_title") }}</v-card-title>
          <v-card-subtitle class="mb-0"> {{ $t("settings.workplace_users_list_subtitle") }}</v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-btn
            data-cy="invite-new-user-btn"
            v-if="$can('update', 'organization user list')"
            @click="inviteTeammateHandler"
            :loading="inviteTeammateBtnLoading"
            color="primary"
            class="left-icon">
            <v-icon size="17" class="mr-2">mdi-account-plus</v-icon>
            {{ $t("settings.workplace_users_invite") }}
          </v-btn>
        </v-card-actions>
      </div>
      <OrganizationUsersList
        :loading="usersListLoading"
        :organization-item="currentlySelectedOrganization"></OrganizationUsersList>
    </v-card>
  </div>
</template>

<script>
  // Vuex
  import store from "@/store";
  import { mapGetters, mapActions } from "vuex";
  // Components
  import OrganizationUsersList from "@/components/lists/OrganizationUsersList";
  import Modal from "@/components/custom/Modal";
  import PaywallLimitModal from "@/components/custom/PaywallLimitModal.vue";
  // Constants
  import { ROLE_OWNER, USER_ROLES, ORGANIZATION_DELETE_CONFIRMATION_WORD } from "@/constant";
  // Helpers
  import { validateEmail } from "@/assets/utilities/helpers/email";
  import { getRoleInfo } from "@/assets/utilities/helpers/roles";
  import cloneDeep from "lodash/cloneDeep";
  // Mixins
  import paywallLimits from "@/mixins/paywallLimits";
  // Utility
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "WorkplaceUsers",
    components: {
      OrganizationUsersList,
      Modal,
      PaywallLimitModal,
    },
    mixins: [paywallLimits],
    data() {
      return {
        store,
        // This is an actual Organization object, not UserOrganization.
        currentlySelectedOrganization: {},
        validateEmail,
        USER_ROLES,
        // All possible roles(permissions) for selected organization.
        organizationRoles: [],
        selectedRole: {},
        companyData: {
          id: "",
          name: "",
        },
        inviteUserData: {
          email: "",
          permission: "",
          domains: [],
          allDomainsAccess: false,
        },
        organizationDomains: [],
        // Loading states
        updateCompanyLoading: false,
        inviteUserLoading: false,
        usersListLoading: false,
        inviteUserModalDisplayed: false,
        isFormValid: false,
        displayDeleteOrganizationModal: false,
        deleteOrganizationLoading: false,
        confirmationWord: "",
        ORGANIZATION_DELETE_CONFIRMATION_WORD,
        displayPaywallLimitModal: false,
        inviteTeammateBtnLoading: false,
      };
    },
    computed: {
      ...mapGetters({
        currentOrganization: "getActiveOrganization",
      }),
      inviteUserBtnDisabled() {
        return (
          !this.inviteUserData.email ||
          !this.inviteUserData.permission ||
          !this.inviteUserData.domains.length ||
          !validateEmail(this.inviteUserData.email)
        );
      },
      domainsList() {
        // If all connected domains is selected, disable all other options
        if (this.inviteUserData.allDomainsAccess) {
          return this.organizationDomains.map(domain => {
            if (domain["@id"] !== "all_domains") {
              return {
                ...domain,
                disabled: true,
              };
            }

            return domain;
          });
        } else {
          return this.organizationDomains.map(domain => {
            return {
              ...domain,
              disabled: false,
            };
          });
        }
      },
    },
    watch: {
      domainsList() {
        /**
         * Override cached items in autocomplete component.
         * This is needed in order for autocomplete items to be disabled / enabled properly.
         */
        if (this.$refs?.domainsInput?.$refs?.autocomplete) {
          this.$refs.domainsInput.$refs.autocomplete.cachedItems = this.domainsList;
        }
      },
      /**
       * User might change the currently active organization
       */
      currentOrganization: {
        immediate: true,
        async handler(newOrganization) {
          if (!newOrganization) {
            this.organizationDomains = [];
            this.currentlySelectedOrganization = null;
            this.companyData.name = "";
            this.companyData.id = null;
            this.organizationRoles = [];

            return;
          }
          this.usersListLoading = true;
          const organizationData = await this.getOrganization(newOrganization.organization.id);
          this.organizationDomains = [
            { name: "All connected domains", "@id": "all_domains" },
            ...organizationData.domains,
          ];
          this.currentlySelectedOrganization = organizationData;
          this.usersListLoading = false;
          this.companyData.name = organizationData.name;
          this.companyData.id = organizationData.id;
          this.organizationRoles =
            organizationData?.organizationPermissions
              .map(organizationPermission => {
                return {
                  ...organizationPermission,
                  ...getRoleInfo(organizationPermission),
                };
              })
              // Remove Owner role from selection.
              .filter(organizationPermission => organizationPermission.value !== ROLE_OWNER.value) || [];
        },
      },
    },
    methods: {
      ...mapActions({
        refreshCurrentlyActiveOrganizationData: "refreshCurrentlyActiveOrganizationData",
        logout: "auth/logout",
      }),
      handleDomainsBlur() {
        /**
         * There's a minor issue with vuetify's autocomplete -
         * Whenever user inputs something to search field, selects an item and clicks away,
         * input placeholder is not visible. That's because internally, autocomplete keeps search value and doesn't clear it.
         */
        setTimeout(() => {
          this.$refs.domainsInput.search = "";
        }, 100);
      },
      async inviteTeammateHandler() {
        if (this.ignorePaywallLimits) {
          this.inviteUserModalDisplayed = true;

          return;
        }

        this.inviteTeammateBtnLoading = true;
        await this.updateLimitExceededData();
        if (this.limitsExceeded["teammates"]) {
          this.displayPaywallLimitModal = true;
        } else {
          this.inviteUserModalDisplayed = true;
        }
        this.inviteTeammateBtnLoading = false;
      },
      selectedDomainsHandler(selectedDomains) {
        this.inviteUserData.allDomainsAccess = selectedDomains.includes("all_domains");
        if (this.inviteUserData.allDomainsAccess) {
          this.inviteUserData.domains = ["all_domains"];
        }
      },
      closeDeleteOrganizationModal() {
        this.displayDeleteOrganizationModal = false;
        this.confirmationWord = "";
      },
      async deleteOrganization() {
        this.deleteOrganizationLoading = true;
        try {
          await this.$admin.dataProvider.delete(`organizations`, {
            id: store.state.currentlyActiveOrganization.organization.id,
          });
          await this.refreshCurrentlyActiveOrganizationData();
          if (!store.state.currentlyActiveOrganization) {
            this.$store.commit("SET_SKIP_LOGOUT_REDIRECT", true);
            await this.logout();
            this.$router.push({ name: "redirect", query: { type: "organization" } });
          }
          this.$admin.toast.success(this.$t("settings.delete_organization_success"));
        } catch (err) {
          this.$admin.toast.error(formErrorMessage(err, this.$t("error")));
        } finally {
          this.deleteOrganizationLoading = false;
          this.displayDeleteOrganizationModal = false;
          this.confirmationWord = "";
        }
      },
      async inviteUser() {
        this.inviteUserLoading = true;
        try {
          const inviteUserData = cloneDeep(this.inviteUserData);
          if (inviteUserData.allDomainsAccess) {
            // Empty out the array so that "all_domains" value is not sent
            inviteUserData.domains = [];
          }

          const { data } = await this.$admin.dataProvider.customHttpCall(
            "POST",
            `organizations/${store.state.currentlyActiveOrganization.organization.id}/invite`,
            inviteUserData,
          );

          this.$admin.toast.success(data.message);
          // Update the users list
          this.currentlySelectedOrganization = await this.getOrganization(
            store.state.currentlyActiveOrganization.organization.id,
          );
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("error")));
        } finally {
          this.inviteUserLoading = false;
          this.inviteUserModalDisplayed = false;
          this.resetInviteUserData();
        }
      },
      selectedRoleHandler(val) {
        for (let organizationItem of this.organizationRoles) {
          if (organizationItem["@id"] === val) this.selectedRole = organizationItem;
        }
      },
      resetInviteUserData() {
        this.inviteUserData = {
          email: "",
          permission: "",
          domains: [],
          allDomainsAccess: false,
        };
        this.selectedRole = {};
      },

      async getOrganization(organizationId) {
        try {
          const { data } = await this.$admin.dataProvider.getOne("organizations", {
            id: organizationId,
          });

          return data;
        } catch (err) {
          this.$admin.toast.error(formErrorMessage(err, this.$t("error")));
        }
      },
      async updateCompanyDetails() {
        this.updateCompanyLoading = true;
        try {
          if (
            await this.$admin.dataProvider.update("organizations", {
              id: this.companyData.id,
              data: {
                name: this.companyData.name,
              },
            })
          ) {
            this.$admin.toast.success(this.$t("settings.workplace_users_company_updated"));
            await this.refreshCurrentlyActiveOrganizationData();
          }
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("error")));
          console.error(e);
        } finally {
          this.updateCompanyLoading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../../@core/preset/preset/typography/index.scss";
  @import "../../../@core/preset/preset/functions.scss";
  .invite-user__content-wrapper {
    margin-top: 36px;
  }
  .invite-user__role-disclaimer {
    margin-top: 8px;
    @include text-caption();
    color: get-color("subtitle");
  }

  .delete-organization-block {
    & > div:first-child {
      max-width: 863px;
    }
    &__title {
      @include text-body-2-bold();
      color: get-color("subtitle");
      margin-bottom: 6px;
    }
    &__subtitle {
      @include text-subtitle-2();
      color: get-color("subtitle");
    }
  }

  .delete-confirm {
    @include text-subtitle-2();
    color: get-color("body");
    margin-top: 24px;
    margin-bottom: 16px;
    &__input {
      padding-top: 0 !important;
    }
  }

  .company-save-btn.v-btn--disabled {
    border-color: get-color("disabled") !important;
    ::v-deep span {
      color: get-color("disabled") !important;
    }
  }
</style>
